import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Zap } from "lucide-react";

// Brief boost tiers → Stripe subscription lookup keys
const LOOKUP_BY_TIER = {
  spotlight: "brief_boost_7d",
  priority:  "brief_boost_30d",
};

export default function BoostBrief() {
  const { briefId } = useParams();
  const [plans, setPlans] = useState({});
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/brief-featured-plans").then(({ data }) => setPlans(data.plans || {}));
    api.get(`/briefs/${briefId}`).then(({ data }) => setBrief(data)).catch(() => {});
  }, [briefId]);

  const start = async (tier) => {
    setLoading(tier);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        lookup_key: LOOKUP_BY_TIER[tier],
        target_id: briefId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setLoading(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setLoading(null);
    }
  };

  const perks = {
    spotlight: ["Pinned at top of clearance requests while active", "Boosted badge on your request", "Auto-renews every 7 days"],
    priority:  ["Pinned at top", "Boosted badge + priority in email digest", "Auto-renews every 30 days"],
  };

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14">
      <div className="overline text-safety">Boost your request</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter text-forest">
        Get quoted faster
      </h1>
      {brief && (
        <p className="mt-3 text-muted-foreground" data-testid="boost-brief-title">
          For: <span className="font-medium text-foreground">{brief.title}</span>
        </p>
      )}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(plans).map(([id, plan], idx) => (
          <div
            key={id}
            data-testid={`boost-plan-${id}`}
            className={`border bg-white p-8 flex flex-col ${idx === 1 ? "border-forest md:-translate-y-2 relative" : "border-border"}`}
          >
            {idx === 1 && (
              <div className="absolute -top-3 left-8 bg-safety text-white overline px-3 py-1 text-[10px]">Best value</div>
            )}
            <div className="overline text-safety">{id}</div>
            <div className="mt-2 font-display font-black text-4xl text-forest tabular-nums">£{plan.amount}</div>
            <div className="mt-1 text-sm text-muted-foreground">{plan.label}</div>
            <ul className="mt-6 space-y-3 text-sm flex-1">
              {(perks[id] || []).map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-forest mt-0.5" /> {p}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => start(id)}
              disabled={loading === id}
              data-testid={`boost-buy-${id}`}
              className={`mt-8 rounded-none h-12 ${idx === 1 ? "bg-safety hover:bg-safety-hover text-white" : "bg-forest hover:bg-forest-800 text-white"}`}
            >
              {loading === id ? "Redirecting…" : (<><Zap className="w-4 h-4 mr-2" /> Boost with {id}</>)}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Link to="/dashboard/briefs" className="text-sm text-muted-foreground hover:text-forest overline">← Back to my requests</Link>
        <div className="text-xs text-muted-foreground">Secure payments powered by Stripe.</div>
      </div>
    </div>
  );
}
